import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Platform } from 'react-native-web';

import FeatureFlags from '../../../modules/FeatureFlags';
import Janitor from '../../../modules/Janitor';
import analytics from '../../../modules/analytics';
import * as appointmentVariation from '../../../modules/provider/appointmentVariation';
import SupportServiceWrapper from '../../../modules/SupportServiceWrapper';
import { getAndroidVersion, getIOSVersion } from '../../../modules/AppInfo';
import { AUTH_ORIGINS } from '../../../modules/user/constants';
import { addQueryStringParams } from '../../../modules/url';
import { requireLogin, openModal } from '../login/SignUpLoginModal';
import { APP } from '../../../config';

import withStore from '../../withStore';
import Sidebar from './Sidebar';
import { LegalModal } from '../legal/LegalModal';

const SidebarConnector = props => (
  <Sidebar
    {...props}
  />
);

SidebarConnector.propTypes = Sidebar.propTypes;
SidebarConnector.defaultProps = Sidebar.defaultProps;

/**
 * Connects the `Sidebar` component with appropriate data sources
 */
export class SidebarContainer extends React.Component {
  static propTypes = {
    linkTo: PropTypes.func.isRequired,
    /** True if we should render for a standalone page (full width), otherwise false */
    isStandalonePage: PropTypes.bool,
    isProvider: PropTypes.bool,
    isLoggedIn: PropTypes.bool.isRequired,
    searchQuery: PropTypes.string.isRequired,
    providerId: PropTypes.number,
    open: PropTypes.bool.isRequired,
    closeSidebar: PropTypes.func.isRequired,
    /** Given a close callback, return an object with `open` and `close` functions which will be
     * called when the state changes */
    getAnimator: PropTypes.func.isRequired,
    /** Object of utms */
    utmParams: PropTypes.object,
  };

  static defaultProps = {
    providerId: undefined,
    isProvider: false,
    isStandalonePage: false,
    utmParams: {},
  };

  state = {
    smsSupportEnabled: false,
    isSignupAllowed: false,
    showSearchModal: false,
    showLegalModal: false,
    legalDoc: undefined,
  };

  janitor = new Janitor();

  animator = this.props.getAnimator(this.props.closeSidebar);

  componentDidMount() {
    this.janitor.addPromise(Promise.all([
      FeatureFlags.isEnabled('sms_text_support_20190909'),
      appointmentVariation.allowed(),
    ])).then(([smsSupportEnabled, isSignupAllowed]) => (
      this.setState({ smsSupportEnabled, isSignupAllowed })
    ));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open !== this.props.open) {
      // if the redux state has changed, make sure the UI reflects that
      if (this.props.open) {
        this.animator.open();
      } else {
        this.animator.close();
      }
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  /**
   * Open the Terms of service or privacy policy in a modal
   */
  openLegalModal = legalDoc => {
    this.handleClose();
    this.setState({ showLegalModal: true, legalDoc });
  };

  /**
   * Closes the legal terms modal
   */
  handleCloseLegalModal = () => {
    this.setState({ showLegalModal: false, legalDoc: undefined });
  };

  /**
   * Handles tracking of SMS support clicks
   */
  handleSMSSupportClick = () => {
    analytics.track('pro_sms_support_click', { where: 'hamburger menu' });
  };

  /**
   * Handles closing of the sidebar
   */
  handleClose = () => {
    const { closeSidebar } = this.props;
    closeSidebar();
  };

  /**
   * Handles clicks on the provider signup link
   */
  handleProviderSignup = event => {
    if (event) event.preventDefault();
    this.handleClose();

    const { linkTo, utmParams } = this.props;

    if (APP) {
      linkTo('provider_signup.signup_or_login');
    } else {
      openModal('signup', AUTH_ORIGINS.SIDEBAR, utmParams);
    }
  };

  /**
   * Handles clicks on the blog link
   */
  handleBlogClick = event => {
    if (event) event.preventDefault();

    if (Platform.OS === 'web') {
      window.open(addQueryStringParams('https://www.styleseat.com/blog/', this.props.utmParams), '_system');
    } else {
      // Not implemented
    }
  };

  /**
   * Handles clicks on social links
   */
  handleSocialClick = (event, network) => {
    const isIOS = !!getIOSVersion();
    let fallbackURL;
    let appURL;
    let appID;
    let channel;

    switch (network) {
      case 'facebook':
        fallbackURL = 'https://www.facebook.com/484439085178';
        appURL = (isIOS) ? 'https://www.facebook.com/484439085178' : 'fb://page/484439085178';
        appID = (isIOS) ? 'fb://' : 'com.facebook.katana';
        channel = network;
        break;
      case 'instagram':
        fallbackURL = 'https://instagram.com/styleseat/';
        appURL = 'instagram://user?username=styleseat';
        appID = (isIOS) ? 'instagram://' : 'com.instagram.android';
        channel = network;
        break;
      case 'twitter':
        fallbackURL = 'https://twitter.com/styleseat';
        appURL = 'twitter://user?screen_name=styleseat';
        appID = (isIOS) ? 'twitter://' : 'com.twitter.android';
        channel = network;
        break;
      default:
        fallbackURL = 'https://www.pinterest.com/styleseat/';
        appURL = 'pinterest://user/styleseat/';
        appID = (isIOS) ? 'pinterest://' : 'com.pinterest.android';
        channel = network;
        break;
    }

    analytics.track('C Clicked Follow SS', {
      follow_channel: channel,
      follow_source: 'sidebar',
    });

    if ((isIOS || getAndroidVersion()) && window.appAvailability) {
      window.appAvailability.check(
        appID,
        () => {
          window.open(appURL, '_system');
        },
        () => {
          window.open(fallbackURL, '_system');
        },
      );
      event.preventDefault();
    }

    this.handleClose();
  };

  /**
   * Handles opening of the support page
   */
  handleOpenSupport = () => {
    SupportServiceWrapper.open();
  };

  /**
   * Handles opening of client splash welcome screen.
   */
  handleOpenClientSplash = () => {
    analytics.track('client_how_to_book_an_appointment_clicked');
    this.props.linkTo('signup_wall.main', {
      signupWallScreen: 'splash',
      source: 'sidebar',
    });
  };

  /**
   * Handles clicks on the login button
   */
  handleLogin = async () => {
    this.handleClose();
    const { userData } = await requireLogin({ mode: 'login', origin: AUTH_ORIGINS.SIDEBAR });
    if (userData && userData.provider_id) {
      this.props.linkTo('provider', { id: userData.provider_id });
    }
  };

  /**
   * Handles clicks on the sign up button
   */
  handleSignup = async () => {
    this.handleClose();
    const { userData } = await requireLogin({ mode: 'signup', origin: AUTH_ORIGINS.SIDEBAR });
    if (userData && userData.provider_id) {
      this.props.linkTo('provider', { id: userData.provider_id });
    }
  };

  handleToggleSearchModal = isOpen => {
    this.setState({ showSearchModal: isOpen });
  };

  handleSearchPopoverSubmit = (_1, _2, slugged) => {
    this.props.linkTo('search', slugged);
    this.handleClose();
    this.handleToggleSearchModal(false);
  };

  render() {
    const {
      open,
      isStandalonePage,
      utmParams,
      searchQuery,
      providerId,
    } = this.props;
    const {
      smsSupportEnabled,
      isSignupAllowed,
      showSearchModal,
      showLegalModal,
      legalDoc,
    } = this.state;

    if (!open && !isStandalonePage) {
      return null;
    }

    return (
      <React.Fragment>
        <SidebarConnector
          {...this.props}
          searchQuery={searchQuery}
          isSignupAllowed={isSignupAllowed}
          isSMSSupportEnabled={smsSupportEnabled}
          onBlogClick={this.handleBlogClick}
          onClose={this.handleClose}
          onLogin={this.handleLogin}
          onProviderSignup={this.handleProviderSignup}
          onSMSSupportClick={this.handleSMSSupportClick}
          onSignup={this.handleSignup}
          onSocialClick={this.handleSocialClick}
          openPrivacy={() => this.openLegalModal('privacy')}
          openTerms={() => this.openLegalModal('tos')}
          onOpenSupport={this.handleOpenSupport}
          onOpenClientSplash={this.handleOpenClientSplash}
          utmParams={utmParams}
          showSearchModal={showSearchModal}
          onSearchPopoverSubmit={this.handleSearchPopoverSubmit}
          onToggleSearchModal={this.handleToggleSearchModal}
        />
        {showLegalModal && (
          <React.Fragment>
            {legalDoc === 'privacy' && (
              <LegalModal doc="privacy_policy" onDismiss={this.handleCloseLegalModal} />
            )}
            {legalDoc === 'tos' && providerId && (
              <LegalModal doc="pro_terms_of_service" onDismiss={this.handleCloseLegalModal} />
            )}
            {legalDoc === 'tos' && !providerId && (
              <LegalModal doc="consumer_terms_of_service" onDismiss={this.handleCloseLegalModal} />
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  isLoggedIn: !state.user.is_anon,
  providerId: state.user.providerId,
  isProvider: state.user.providerId && !state.user.is_anon,
  searchQuery: state.route.params.q || '',
  open: state.sidebar.open,
  utmParams: {
    utm_source: state.route.params.utm_source,
    utm_campaign: state.route.params.utm_campaign,
    utm_content: state.route.params.utm_content,
    utm_medium: state.route.params.utm_medium,
    utm_term: state.route.params.utm_term,
  },
});

const mapDispatchToProps = dispatch => ({
  linkTo: (route, params) => dispatch.route.go({
    route,
    params,
    notify: true,
  }),
  closeSidebar: dispatch.sidebar.close,
});

export default withStore(connect(mapStateToProps, mapDispatchToProps)(SidebarContainer));
